import { useEffect, useRef, useState } from "react";
import { IconMic } from "@veto/ui";

/** Tap to start/stop voice note recording — encrypted via attachment pipeline. */
export function VoiceNoteButton({
  disabled,
  onRecorded,
  onError,
}: {
  disabled?: boolean;
  onRecorded: (blob: Blob, durationMs: number) => void;
  onError?: (message: string) => void;
}) {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const mediaRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAt = useRef(0);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
      mediaRef.current?.stream.getTracks().forEach((t) => t.stop());
    };
  }, []);

  async function start() {
    if (disabled || recording) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      onError?.("Microphone unavailable in this browser");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      // AAC/MP4 first: it plays everywhere, including the native iPhone app (AVFoundation has no
      // WebM/Opus). Chromium ≥126 and Safari record it; Firefox falls back to WebM/Opus.
      const mime = [
        "audio/mp4;codecs=mp4a.40.2",
        "audio/mp4",
        "audio/webm;codecs=opus",
        "audio/webm",
      ].find((t) => MediaRecorder.isTypeSupported(t)) ?? "";
      let rec: MediaRecorder;
      try {
        rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      } catch {
        stream.getTracks().forEach((t) => t.stop());
        onError?.("Voice recording not supported in this browser");
        return;
      }
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const durationMs = Date.now() - startedAt.current;
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        if (blob.size > 0 && durationMs > 200) {
          onRecorded(blob, durationMs);
        } else {
          onError?.("Voice note too short — hold a bit longer");
        }
      };
      mediaRef.current = rec;
      startedAt.current = Date.now();
      setElapsed(0);
      setRecording(true);
      timerRef.current = window.setInterval(() => {
        setElapsed(Date.now() - startedAt.current);
      }, 200);
      rec.start(100);
    } catch {
      onError?.("Microphone permission denied or unavailable");
    }
  }

  function stop() {
    if (!recording) return;
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = null;
    setRecording(false);
    mediaRef.current?.stop();
    mediaRef.current = null;
  }

  const secs = Math.max(1, Math.round(elapsed / 1000));
  return (
    <button
      type="button"
      className={`icon-btn voice-btn${recording ? " recording" : ""}`}
      aria-label={recording ? "Stop recording" : "Record voice note"}
      title={recording ? `Stop (${secs}s)` : "Voice note"}
      disabled={disabled}
      onClick={() => {
        if (recording) stop();
        else void start();
      }}
    >
      {recording ? (
        // Fixed-width timer text avoids Playwright "element is not stable" from 200ms reflows.
        <span className="voice-timer" style={{ fontVariantNumeric: "tabular-nums", minWidth: "2.5ch" }}>
          {secs}s
        </span>
      ) : (
        <IconMic />
      )}
    </button>
  );
}
